import React, { useEffect, useMemo, useState } from 'react';

const TRAINING_VIDEOS = [
  { title: 'Academic Eye Overview Video', url: 'https://canva.link/qtek6v9wzkiokc4' },
  { title: 'Student Profile Data Entry', url: 'https://www.loom.com/share/cba325bfa478410a8ed84d2a023b7656' },
  { title: 'Data Entry - Multiple Students', url: 'https://www.loom.com/share/a1bafe282443412eab90d2e2ab847958' },
];

const STANDARD_MAP_ASSET_CANDIDATES = [
  '/Academic%20Eye%20Standard%20Map.png',
  '/standard-map-core-subjects-pk-12.png',
  '/standard-map-core-subjects-pk-12.pdf',
];
const STANDARD_STRUCTURE_ASSET = '/Academic%20Eye%20Standard%20Structure%20(1).pdf';

function verifyImageAsset(candidate) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = candidate;
  });
}

async function verifyPdfAsset(candidate) {
  try {
    const response = await fetch(candidate, { method: 'GET' });
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    return response.ok && contentType.includes('application/pdf');
  } catch (_err: any) {
    return false;
  }
}

export default function TrainingResourcesPage({ role = 'teacher' }: any) {
  const showStandardMap = role === 'admin';
  const [standardMapAsset, setStandardMapAsset] = useState<any>('');
  const [assetLoading, setAssetLoading] = useState<any>(true);
  const [assetVerified, setAssetVerified] = useState<any>(false);
  const [structureVerified, setStructureVerified] = useState<any>(false);

  const audienceLabel = useMemo(() => {
    return role === 'admin' ? 'Leadership and school teams' : 'Teachers and instructional teams';
  }, [role]);

  useEffect(() => {
    if (!showStandardMap) return;
    let cancelled = false;

    async function resolveAsset() {
      setAssetLoading(true);
      setAssetVerified(false);
      for (const candidate of STANDARD_MAP_ASSET_CANDIDATES) {
        try {
          const isPdf = candidate.toLowerCase().endsWith('.pdf');
          const isValidAsset = isPdf
            ? await verifyPdfAsset(candidate)
            : await verifyImageAsset(candidate);
          if (isValidAsset) {
            if (!cancelled) {
              setStandardMapAsset(candidate);
              setAssetVerified(true);
              setAssetLoading(false);
            }
            return;
          }
        } catch (_err: any) {
          // Continue to the next candidate.
        }
      }

      if (!cancelled) {
        setStandardMapAsset('');
        setAssetVerified(false);
        setAssetLoading(false);
      }
    }

    resolveAsset();
    return () => {
      cancelled = true;
    };
  }, [showStandardMap]);

  useEffect(() => {
    let cancelled = false;

    async function resolveStructureAsset() {
      const isValid = await verifyPdfAsset(STANDARD_STRUCTURE_ASSET);
      if (!cancelled) {
        setStructureVerified(isValid);
      }
    }

    resolveStructureAsset();
    return () => {
      cancelled = true;
    };
  }, []);

  const isPdfAsset = standardMapAsset.toLowerCase().endsWith('.pdf');
  const canOpenStandardMap = Boolean(standardMapAsset && assetVerified);
  const expectedAssetList = STANDARD_MAP_ASSET_CANDIDATES.join(', ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <section
        style={{
          background: 'linear-gradient(135deg, #27459b 0%, #1e3a8a 100%)',
          color: '#ffffff',
          borderRadius: 18,
          padding: '24px 26px',
          boxShadow: '0 12px 32px rgba(30, 58, 138, 0.18)',
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Training & Resources</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.86)', maxWidth: 780, lineHeight: 1.6 }}>
          {audienceLabel} can use {showStandardMap ? 'the Standard Map and progression reference tools' : 'training videos and the Standard Structure reference'} here to align data entry, review subject relationships, and move between planning and proficiency workflows faster.
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Training</div>
          <section
            style={{
              background: '#ffffff',
              border: '1px solid #dbe4f0',
              borderRadius: 18,
              padding: 20,
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {TRAINING_VIDEOS.map((video) => (
                <div
                  key={video.title}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', padding: 16, border: '1px solid #dbe4f0', borderRadius: 12, background: '#f8fafc' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{ width: 42, height: 42, display: 'grid', placeItems: 'center', flex: '0 0 auto', borderRadius: 10, background: '#dbeafe', color: '#1d4ed8', fontSize: 20 }}>▶</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: '#1e3a5f', fontSize: 15 }}>{video.title}</div>
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>Training video</div>
                    </div>
                  </div>
                  <a href={video.url} target="_blank" rel="noopener noreferrer" style={{ background: '#1d4ed8', color: '#ffffff', borderRadius: 8, padding: '9px 13px', fontSize: 12, fontWeight: 800, textDecoration: 'none' }}>Open</a>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Resources</div>
          {showStandardMap && <section
        style={{
          background: '#ffffff',
          border: '1px solid #dbe4f0',
          borderRadius: 18,
          padding: 20,
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1e3a5f' }}>Standard Map</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              Core subjects PK-12 map for cross-subject reference inside Training & Resources.
            </div>
          </div>
        </div>

        {!assetLoading && !canOpenStandardMap && (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 12,
              background: '#fff7ed',
              border: '1px solid #fdba74',
              color: '#9a3412',
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            Standard Map file not deployed yet. Expected one of: <strong>{expectedAssetList}</strong>
          </div>
        )}

        {assetLoading ? (
          <div style={{ border: '1px solid #dbe4f0', borderRadius: 12, background: '#f8fafc', padding: '16px 18px', color: '#475569', fontSize: 14 }}>
            Checking resource availability...
          </div>
        ) : canOpenStandardMap ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', padding: 16, border: '1px solid #dbe4f0', borderRadius: 12, background: '#f8fafc' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <div style={{ width: 42, height: 42, display: 'grid', placeItems: 'center', flex: '0 0 auto', borderRadius: 10, background: '#dbeafe', color: '#1d4ed8', fontSize: 22 }}>▣</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, color: '#1e3a5f', fontSize: 15 }}>Academic Eye Standard Map - Core Subjects PK-12</div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>{isPdfAsset ? 'PDF document' : 'Image reference'} available</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a href={standardMapAsset} target="_blank" rel="noopener noreferrer" style={{ background: '#1d4ed8', color: '#ffffff', borderRadius: 8, padding: '9px 13px', fontSize: 12, fontWeight: 800, textDecoration: 'none' }}>Open</a>
              <a href={standardMapAsset} download style={{ background: '#ffffff', color: '#1e3a5f', border: '1px solid #b8c8dc', borderRadius: 8, padding: '9px 13px', fontSize: 12, fontWeight: 800, textDecoration: 'none' }}>Download</a>
            </div>
          </div>
        ) : (
          <div
            style={{
              border: '1px dashed #cbd5e1',
              borderRadius: 16,
              background: '#f8fafc',
              padding: '28px 24px',
              color: '#475569',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>Standard Map asset not found yet</div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              Add and deploy one of these assets to the frontend public bundle and this section will render it automatically for teachers and leadership: <strong>{expectedAssetList}</strong>.
            </div>
          </div>
        )}
      </section>}

      <section
        style={{
          background: '#ffffff',
          border: '1px solid #dbe4f0',
          borderRadius: 18,
          padding: 0,
          overflow: 'hidden',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        <div style={{ padding: '18px 20px 0' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1e3a5f' }}>Standard Structure</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, marginBottom: 16 }}>
            Use the Standard Structure PDF below{showStandardMap ? ' alongside the Standard Map' : ''} when reviewing domains, strands, and cluster relationships.
          </div>
        </div>
        <div style={{ padding: '0 20px 20px' }}>
          {structureVerified ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', padding: 16, border: '1px solid #dbe4f0', borderRadius: 12, background: '#f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={{ width: 42, height: 42, display: 'grid', placeItems: 'center', flex: '0 0 auto', borderRadius: 10, background: '#dbeafe', color: '#1d4ed8', fontSize: 22 }}>▤</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: '#1e3a5f', fontSize: 15 }}>Academic Eye Standard Structure</div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>PDF reference for domains, strands, and cluster relationships</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <a href={STANDARD_STRUCTURE_ASSET} target="_blank" rel="noopener noreferrer" style={{ background: '#1d4ed8', color: '#ffffff', borderRadius: 8, padding: '9px 13px', fontSize: 12, fontWeight: 800, textDecoration: 'none' }}>Open</a>
                <a href={STANDARD_STRUCTURE_ASSET} download style={{ background: '#ffffff', color: '#1e3a5f', border: '1px solid #b8c8dc', borderRadius: 8, padding: '9px 13px', fontSize: 12, fontWeight: 800, textDecoration: 'none' }}>Download</a>
              </div>
            </div>
          ) : (
            <div
              style={{
                border: '1px dashed #cbd5e1',
                borderRadius: 16,
                background: '#f8fafc',
                padding: '28px 24px',
                color: '#475569',
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>Standard Structure asset not found yet</div>
              <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                Add and deploy <strong>{STANDARD_STRUCTURE_ASSET}</strong> to the frontend public bundle and this section will render it automatically.
              </div>
            </div>
          )}
        </div>
      </section>
        </div>
      </div>
    </div>
  );
}